import * as amplitude from "@amplitude/analytics-browser";
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser";
import {amplitudeToken, env} from "@bounce/shared/constants";
import {
  extractFeatureFlagValues,
  GeneralEventProps,
  IPageClickEvent,
  IPageViewEvent,
  PageClickEventProps,
  PagesURL,
  PageViewEventProps,
  UserStep,
} from "./amplitude-utils";
import {AmplitudeLogger} from "@bounce/shared/helpers";
import {useAuth} from "@bounce/shared/contexts";
import {isPreLoginRole} from "~/helpers/roles";
import {get, indexOf, isEmpty, reduce} from "lodash";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useLocation} from "react-router-dom";
import {useGetAccount} from "~/api/requests";
import {useConsumerUser} from "~/contexts";

interface AmplitudeContextProps {
  pageClickEvent: ({page, cta, data}: IPageClickEvent) => void;
  pageViewEvent: ({page, data, user_step}: IPageViewEvent) => void;
  setError: (error: string | undefined) => void;
  sessionId: number | undefined;
}

const AmplitudeContext = createContext<AmplitudeContextProps>(undefined!);

export const useAmplitude = (): AmplitudeContextProps => {
  const context = useContext(AmplitudeContext);
  if (!context) throw new Error("AmplitudeContext must be used inside AmplitudeProvider");
  return context;
};

export const AmplitudeProvider = ({children}: PropsWithChildren) => {
  const enabled = ["production"].includes(env);
  const {loggedIn, userRole} = useAuth();
  const {userContext} = useConsumerUser();
  const [accountId, setAccountId] = useState<string>();
  const defaultUTM = JSON.parse(sessionStorage.getItem("UTM") || "{}");
  const [UTM, setUTM] = useState(defaultUTM);
  const error = useRef<string | undefined>(undefined);
  const currentUrl = window.location.href;
  const isOnAccountsUrl = [PagesURL.ACCOUNTS, PagesURL.ACCOUNT].some((page) => currentUrl.endsWith(page));
  const isOnAccountDashboardUrl = currentUrl.includes(PagesURL.ACCOUNT_DASHBOARD);
  const [amplitudeUserId, setAmplitudeUserId] = useState<string>();
  const {search, pathname} = useLocation();
  const searchParams = new URLSearchParams(search);
  const shouldNotTrack = !enabled;
  const formattedFeatureFlags = extractFeatureFlagValues(userContext?.feature_flags);
  const setError = (currError?: string) => {
    error.current = currError;
  };

  const {data: account} = useGetAccount({accountId}, {enabled: !!(loggedIn && accountId)});
  const activePaymentPlan = useMemo(() => {
    if (account) return account.active_payment_plan;
  }, [account]);

  useEffect(() => {
    const pathParts = pathname.split("/");
    const accountIndex = indexOf(pathParts, "account");
    if (accountIndex !== -1 && accountIndex < pathParts.length - 1) {
      if (!isNaN(parseInt(pathParts[accountIndex + 1]))) {
        setAccountId(pathParts[accountIndex + 1]);
      }
    }
  }, [pathname]);

  const initAmplitude = () => {
    amplitude.init(amplitudeToken, {
      minIdLength: 4,
      userId: amplitudeUserId,
      defaultTracking: {
        sessions: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
      },
      loggerProvider: new AmplitudeLogger(),
    });
    const sessionReplayTracking = sessionReplayPlugin({sampleRate: 0.5});
    amplitude.add(sessionReplayTracking);
  };

  const sessionId = amplitude.getSessionId();

  const saveUTMToStorage = () => {
    if (isEmpty(UTM)) {
      const new_UTMs = reduce(
        Array.from(searchParams.entries()),
        (acc, [key, value]) => {
          return {...acc, [key]: value};
        },
        {}
      );
      sessionStorage.setItem("UTM", JSON.stringify(new_UTMs));
      setUTM(new_UTMs);
    }
  };

  const initUserStep = () => {
    if (isPreLoginRole(userRole)) {
      if (activePaymentPlan) {
        return UserStep.APP;
      }
      return UserStep.ONBOARDING;
    }
    if (!loggedIn) {
      return UserStep.LOGIN;
    }
    if (isOnAccountsUrl) {
      return UserStep.ACCOUNTS;
    }
    if (activePaymentPlan || isOnAccountDashboardUrl) {
      return UserStep.APP;
    }
    return UserStep.ONBOARDING;
  };

  const initUserId = () => {
    if (userContext?.public_id) {
      setAmplitudeUserId(userContext.public_id);
    } else if (get(UTM, "id")) {
      setAmplitudeUserId(get(UTM, "id"));
    }
  };

  const generalProperties: GeneralEventProps = useMemo(() => {
    return {
      app: "consumer-app",
      url: currentUrl,
      env,
      UTM: UTM,
      user_step: initUserStep(),
      event: "",
    };
  }, [UTM, currentUrl]);

  const accountProperties = useMemo(() => {
    if (!isEmpty(account)) {
      return {
        batch: account.batch,
        product: account.product,
        partner_code: account.partner_code,
        account_public_id: account.public_id,
        primary_account: account.primary_account,
        credit_reporting_status: account?.credit_report_details.last_report_type,
      };
    }
    return {};
  }, [account]);

  const pageViewEvent = useCallback(
    ({page, data, user_step}: IPageViewEvent) => {
      if (shouldNotTrack) {
        return;
      }
      const userStep = user_step || initUserStep();
      const properties: PageViewEventProps = {
        page: page,
        event_type: "page_view",
        data: data,
        ...generalProperties,
        ...accountProperties,
      };
      if (userStep === UserStep.LOGIN) {
        properties["event"] = "login_page_view";
        properties["context"] = get(UTM, "id") ? "contextualized_login" : "anonymous_login";
      }
      if (userStep === UserStep.ONBOARDING) {
        properties["event"] = "onboarding_page_view";
      }
      if (userStep === UserStep.APP) {
        properties["event"] = "app_page_view";
      }
      if (userStep === UserStep.ACCOUNTS) {
        properties["event"] = "accounts_page_view";
      }
      properties["feature_flags"] = formattedFeatureFlags;
      amplitude.track(properties["event"], properties);
    },
    [UTM, generalProperties, accountProperties, shouldNotTrack, formattedFeatureFlags]
  );

  const pageClickEvent = useCallback(
    ({page, cta, data}: IPageClickEvent): void => {
      if (shouldNotTrack) {
        return;
      }
      const userStep = initUserStep();
      const properties: PageClickEventProps = {
        page: page,
        event_type: "page_click",
        cta: cta,
        data: data,
        error: error.current || data?.error,
        ...generalProperties,
        ...accountProperties,
      };
      if (userStep === UserStep.LOGIN) {
        properties["event"] = "login_page_clicked";
        properties["context"] = get(UTM, "id") ? "contextualized_login" : "anonymous_login";
      }
      if (userStep === UserStep.ONBOARDING) {
        properties["event"] = "onboarding_page_clicked";
      }
      if (userStep === UserStep.APP) {
        properties["event"] = "app_page_clicked";
      }
      if (userStep === UserStep.ACCOUNTS) {
        properties["event"] = "accounts_page_clicked";
      }
      properties["feature_flags"] = formattedFeatureFlags;
      amplitude.track(properties["event"], properties);
      setError(undefined);
    },
    [UTM, generalProperties, accountProperties, shouldNotTrack, error, formattedFeatureFlags]
  );

  useEffect(() => {
    if (shouldNotTrack) {
      return;
    }
    saveUTMToStorage();
    initAmplitude();
  }, []);

  useEffect(() => {
    initUserId();
  }, [loggedIn, UTM]);

  useEffect(() => {
    initUserStep();
  }, [currentUrl, activePaymentPlan, loggedIn, accountId]);

  useEffect(() => {
    if (shouldNotTrack) {
      return;
    }
    const currentId = amplitude.getUserId();
    if (userContext?.public_id && !currentId) {
      amplitude.setUserId(userContext.public_id);
    }
  }, [userContext, shouldNotTrack]);

  return (
    <AmplitudeContext.Provider
      value={{
        pageClickEvent,
        pageViewEvent,
        setError,
        sessionId,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};
